import React, { useState } from 'react';
import { Download, FileText, FileSpreadsheet } from 'lucide-react';
import { exportToCSV, exportToExcel, exportToBoth, ExportData } from '../../../utils/exportUtils';
import { showSuccessToast, showErrorToast } from '../../../hooks/useToast';
import { logger } from '../../../utils/logger';

interface ExportButtonProps {
  data: ExportData;
  filename?: string;
  disabled?: boolean;
  variant?: 'csv' | 'excel' | 'both';
  className?: string;
  showDropdown?: boolean;
}

const ExportButton: React.FC<ExportButtonProps> = ({
  data,
  filename = 'export',
  disabled = false,
  variant = 'both',
  className = '',
  showDropdown = true
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (exportType: 'csv' | 'excel' | 'both') => {
    if (disabled || isExporting) return;

    setIsExporting(true);
    
    try {
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const baseFilename = `${filename}_${timestamp}`;

      logger.info(`Starting ${exportType} export`, { 
        filename: baseFilename, 
        rowCount: data.rows.length,
        columnCount: data.headers.length 
      }, 'ExportButton');

      switch (exportType) {
        case 'csv':
          exportToCSV(data, { filename: `${baseFilename}.csv` });
          showSuccessToast('Export Complete', 'CSV file downloaded successfully');
          break;
        case 'excel':
          await exportToExcel(data, { filename: `${baseFilename}.xlsx` });
          showSuccessToast('Export Complete', 'Excel file downloaded successfully');
          break;
        case 'both':
          await exportToBoth(data, baseFilename);
          showSuccessToast('Export Complete', 'CSV and Excel files downloaded successfully');
          break;
      }

      logger.info(`${exportType} export completed successfully`, { filename: baseFilename }, 'ExportButton');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Export failed';
      logger.error(`${variant} export failed`, { error: errorMessage }, 'ExportButton');
      showErrorToast('Export Failed', errorMessage);
    } finally {
      setIsExporting(false);
    }
  };

  if (!showDropdown || variant !== 'both') {
    // Single button for specific format
    const icon = variant === 'csv' ? FileText : FileSpreadsheet;
    const label = variant === 'csv' ? 'Export' : 'Export Excel';
    const Icon = icon;

    return (
      <button
        onClick={() => handleExport(variant)}
        disabled={disabled || isExporting}
        className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors
          ${disabled || isExporting
            ? 'bg-disabledBg text-disabledText cursor-not-allowed'
            : 'bg-accent text-textInverse hover:bg-accentStrong focus:outline-none focus:ring-2 focus:ring-[var(--focus-ring)] focus:ring-offset-2'
          } ${className}`}
      >
        <Icon className="w-4 h-4" />
        {isExporting ? 'Exporting...' : label}
      </button>
    );
  }

  // Dropdown for both formats
  return (
    <div className={`relative inline-block ${className}`}>
      <button
        onClick={() => handleExport('both')}
        disabled={disabled || isExporting}
        className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors
          ${disabled || isExporting
            ? 'bg-disabledBg text-disabledText cursor-not-allowed'
            : 'bg-accent text-textInverse hover:bg-accentStrong focus:outline-none focus:ring-2 focus:ring-[var(--focus-ring)] focus:ring-offset-2'
          }`}
      >
        <Download className="w-4 h-4" />
        {isExporting ? 'Exporting...' : 'Export'}
      </button>

      {/* Dropdown menu */}
      {!disabled && !isExporting && (
        <div className="absolute right-0 mt-2 w-48 card-surface border border-tableBorder rounded-lg shadow-elev2 z-10">
          <div className="py-1">
            <button
              onClick={() => handleExport('csv')}
              className="flex items-center gap-3 w-full px-4 py-2 text-sm text-textPrimary hover:bg-tableHover transition-colors"
            >
              <FileText className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={() => handleExport('excel')}
              className="flex items-center gap-3 w-full px-4 py-2 text-sm text-textPrimary hover:bg-tableHover transition-colors"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Export Excel
            </button>
            <button
              onClick={() => handleExport('both')}
              className="flex items-center gap-3 w-full px-4 py-2 text-sm text-textPrimary hover:bg-tableHover transition-colors border-t border-tableBorder"
            >
              <Download className="w-4 h-4" />
              Export Both
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportButton;
